import {useContext} from "react";
import { useQuery } from '@tanstack/react-query';
import {axiosRequest} from "@lib/axios";
import UserContext from "@lib/contexts/userContext";
import { getFullName, getUserLabel } from '@lib/user';
import { sortByLabel } from "@lib/utils";

/**
 * React Query wrapper for the Staff Users (with current user) query.
 */
const useStaffUsersQuery = () => {
  const { authenticated, userRecord } = useContext(UserContext);

  const getStaffUsersWithCurrentUser = async () => {
    try {
      const staffUsers = await axiosRequest({ method: 'GET', url: `auth/users/staff`});
      // Add the current user, if not in the list retrieved from the API
      const staffUser = staffUsers.find((u) => u.email === userRecord.email);
      if (!staffUser) {
        const u = {
          email: userRecord.email,
          firstName: userRecord.firstName,
          lastName: userRecord.lastName,
        };
        u.name = getFullName(u);
        u.label = getUserLabel(u);
        staffUsers.push(u);
      }
      return staffUsers.map(u => ({
        ...u,
        label: getUserLabel(u),
      })).sort(sortByLabel);
    } catch (e) {
      console.log('[getStaffUsers] error:', e);
      return [];
    }
  };

  /**
   * @typedef {import("@tanstack/react-query").UseQueryResult<StaffResult, Error>} StaffQueryResult
   * @typedef {Object} StaffUser
   * @property {string} email
   * @property {string} firstName
   * @property {string} lastName
   * @typedef {StaffUser[]} StaffResult
   */

  /**
   * @returns {StaffQueryResult} The query result object containing data, isLoading, error, etc.
   */
  return useQuery({
    queryKey: ['staffUsersWithCurrentUser'],
    queryFn: getStaffUsersWithCurrentUser,
    enabled: authenticated,
    structuralSharing: false,
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export default useStaffUsersQuery;
